import type { AlarmLevelSettings, AlarmSound } from '@atnt/shared';

/**
 * Alarm tones generated with the Web Audio API (no audio files):
 *  - `signal_deep` ("Signal deep") — three short low beeps (sine 440 Hz + a soft octave below), ~0.9 s;
 *  - `siren` ("Sirena ovozi") — two 650 ↔ 1250 Hz sweeps through a low-pass filter, ~2 s.
 * A playback repeats the pattern `repeatCount` times spread over `durationSec` (and never longer than that).
 * Browsers start audio only after a user gesture: the context is unlocked on the first click / key press, and
 * `blocked` tells the UI to offer an "enable sound" button meanwhile.
 */

type Listener = () => void;

const PATTERN_SEC: Record<AlarmSound, number> = { signal_deep: 0.9, siren: 2 };
const VOLUME = 0.22;

class AlarmSoundPlayer {
  #ctx: AudioContext | null = null;
  #current: { key: string; priority: number; stop: () => void } | null = null;
  #listeners = new Set<Listener>();
  #hooked = false;

  /** Unlock audio on the first user gesture (call once from a mounted component). */
  install() {
    if (this.#hooked || typeof window === 'undefined') return;
    this.#hooked = true;
    const unlock = () => {
      void this.#context()?.resume().then(() => this.#emit());
    };
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
  }

  /** True while the browser still blocks autoplay (no user gesture yet). */
  get blocked(): boolean {
    // no context yet: creating one before a gesture only produces a console warning, so ask the browser instead
    if (!this.#ctx) return typeof navigator === 'undefined' || !(navigator.userActivation?.hasBeenActive ?? false);
    return this.#ctx.state !== 'running';
  }

  /** Key of the alarm being played (event id), or null. */
  get playing(): string | null {
    return this.#current?.key ?? null;
  }

  onChange(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  async resume() {
    await this.#context()?.resume();
    this.#emit();
  }

  /** Play the configured alarm; a lower-priority alarm never interrupts a higher one (M-2 > M-1). */
  play(key: string, settings: Pick<AlarmLevelSettings, 'sound' | 'repeatCount' | 'durationSec'>, priority = 0) {
    const ctx = this.#context();
    if (!ctx) return;
    if (this.#current && this.#current.priority > priority) return;
    this.stop();
    void ctx.resume().catch(() => undefined);
    const master = ctx.createGain();
    master.gain.value = VOLUME;
    master.connect(ctx.destination);
    const pattern = PATTERN_SEC[settings.sound];
    const repeats = Math.max(1, Math.min(100, Math.round(settings.repeatCount)));
    const total = Math.max(pattern, Math.min(3600, settings.durationSec));
    const step = Math.max(pattern + 0.35, total / repeats);
    const t0 = ctx.currentTime + 0.05;
    for (let i = 0; i < repeats && i * step < total; i++) this.#schedule(ctx, master, settings.sound, t0 + i * step);
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearTimeout(timer);
      try {
        master.gain.cancelScheduledValues(ctx.currentTime);
        master.gain.setValueAtTime(0, ctx.currentTime);
        master.disconnect();
      } catch {
        /* already disconnected */
      }
      if (this.#current?.stop === stop) {
        this.#current = null;
        this.#emit();
      }
    };
    const timer = setTimeout(stop, (Math.min(total, repeats * step) + 0.5) * 1000);
    this.#current = { key, priority, stop };
    this.#emit();
  }

  /** One pattern ("Tekshirish"). */
  test(sound: AlarmSound) {
    this.play(`test:${sound}`, { sound, repeatCount: 1, durationSec: PATTERN_SEC[sound] }, 99);
  }

  /** Stop the current sound (or only when it belongs to `key`). */
  stop(key?: string) {
    if (!this.#current || (key && this.#current.key !== key)) return;
    this.#current.stop();
  }

  #emit() {
    this.#listeners.forEach((l) => l());
  }

  #context(): AudioContext | null {
    if (this.#ctx) return this.#ctx;
    const Ctor = typeof window === 'undefined' ? undefined : (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return null;
    this.#ctx = new Ctor();
    this.#ctx.onstatechange = () => this.#emit();
    return this.#ctx;
  }

  #schedule(ctx: AudioContext, out: AudioNode, sound: AlarmSound, at: number) {
    if (sound === 'signal_deep') {
      for (let b = 0; b < 3; b++) {
        const start = at + b * 0.3;
        this.#tone(ctx, out, 'sine', 440, start, 0.18, 1);
        this.#tone(ctx, out, 'triangle', 220, start, 0.18, 0.45);
      }
      return;
    }
    // siren: sawtooth through a low-pass filter, frequency swept up and down twice
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    filter.type = 'lowpass';
    filter.frequency.value = 2200;
    osc.frequency.setValueAtTime(650, at);
    for (let k = 0; k < 2; k++) {
      osc.frequency.linearRampToValueAtTime(1250, at + k + 0.5);
      osc.frequency.linearRampToValueAtTime(650, at + k + 1);
    }
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.5, at + 0.04);
    gain.gain.setValueAtTime(0.5, at + 1.92);
    gain.gain.linearRampToValueAtTime(0, at + 2);
    osc.connect(filter).connect(gain).connect(out);
    osc.start(at);
    osc.stop(at + 2.05);
  }

  #tone(ctx: AudioContext, out: AudioNode, type: OscillatorType, freq: number, at: number, length: number, level: number) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(level, at + 0.012);
    gain.gain.setValueAtTime(level, at + length - 0.04);
    gain.gain.linearRampToValueAtTime(0, at + length);
    osc.connect(gain).connect(out);
    osc.start(at);
    osc.stop(at + length + 0.02);
  }
}

export const alarmSound = new AlarmSoundPlayer();
